/*
 * Purpose: Canvas pan/zoom coordinate helpers for Mission map
 * Last modified: 2026-06-27 | Agent
 * Completeness: 95
 */
import { createContext, useContext, type RefObject } from 'react';

export type CanvasViewport = {
  panX: number;
  panY: number;
  zoom: number;
};

export type CanvasViewportContextValue = {
  viewport: CanvasViewport;
  containerRef: RefObject<HTMLDivElement | null>;
};

export const DEFAULT_CANVAS_VIEWPORT: CanvasViewport = {
  panX: 0,
  panY: 0,
  zoom: 100,
};

const defaultContextValue: CanvasViewportContextValue = {
  viewport: DEFAULT_CANVAS_VIEWPORT,
  containerRef: { current: null },
};

export function viewportScale(viewport: CanvasViewport): number {
  return viewport.zoom / 100;
}

export function screenToWorld(
  clientX: number,
  clientY: number,
  containerRect: DOMRect,
  viewport: CanvasViewport
): { x: number; y: number } {
  const scale = viewportScale(viewport);
  return {
    x: (clientX - containerRect.left - viewport.panX) / scale,
    y: (clientY - containerRect.top - viewport.panY) / scale,
  };
}

export const CanvasViewportContext = createContext<CanvasViewportContextValue>(defaultContextValue);

export function useCanvasViewport(): CanvasViewportContextValue {
  return useContext(CanvasViewportContext);
}

/** World size — room to pan beyond initial viewport */
export const CANVAS_WORLD_WIDTH = 4800;
export const CANVAS_WORLD_HEIGHT = 3200;
